import { createContext, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { roomService, type Room as ServiceRoom } from '../../../shared/services/rooms/roomsServices';
import { useNavigate, useParams } from 'react-router-dom';

export interface Schedule {
  id?: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  interval_minutes: number;
  is_active: boolean;
}

export interface Room extends ServiceRoom {
  tenant_name?: string;
  status?: string;
  capacity?: number;
  location?: string;
  schedules?: Schedule[];
}

interface RoomContextData {
  rooms: Room[];
  isLoading: boolean;
  error: string | null;
  roomData: Room | null;
  isDataLoading: boolean;
  createRoom: (data: Omit<Room, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateRoom: (id: string, data: Partial<Omit<Room, 'id' | 'createdAt' | 'updatedAt'>>) => Promise<void>;
  deleteRoom: (id: string) => Promise<void>;
  isSubmitting: boolean;
  setError: (error: string | null) => void;
  refetchRooms: () => void;
}

interface RoomProviderProps {
  children: ReactNode;
}

export const RoomContext = createContext({} as RoomContextData);

export function RoomProvider({ children }: RoomProviderProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Listagem de rooms
  const { data: rooms = [], isLoading, refetch: refetchRooms } = useQuery({
    queryKey: ['rooms'],
    queryFn: async () => {
      try {
        const response = await roomService.list();
        return response.data as unknown as Room[];
      } catch (error: any) {
        setError(error.message || 'Erro ao buscar áreas');
        return [];
      }
    }
  });

  // Detalhe de room
  const { data: roomData = null, isLoading: isDataLoading } = useQuery({
    queryKey: ['room', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        const response = await roomService.getById(id);
        return response.data as unknown as Room;
      } catch (error: any) {
        setError(error.message || 'Erro ao buscar área');
        return null;
      }
    },
    enabled: !!id,
  });

  // Criação
  const createMutation = useMutation({
    mutationFn: (data: Omit<Room, 'id' | 'createdAt' | 'updatedAt'>) => roomService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      navigate('/rooms');
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || error.message || 'Erro ao criar área');
    }
  });

  // Edição
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Omit<Room, 'id' | 'createdAt' | 'updatedAt'>> }) => roomService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      queryClient.invalidateQueries({ queryKey: ['room', id] });
      navigate('/rooms');
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || error.message || 'Erro ao atualizar área');
    }
  });

  // Deleção
  const deleteMutation = useMutation({
    mutationFn: (id: string) => roomService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      navigate('/rooms');
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || error.message || 'Erro ao excluir área');
    }
  });

  const createRoom = async (data: Omit<Room, 'id' | 'createdAt' | 'updatedAt'>) => {
    await createMutation.mutateAsync(data);
  };

  const updateRoom = async (id: string, data: Partial<Omit<Room, 'id' | 'createdAt' | 'updatedAt'>>) => {
    await updateMutation.mutateAsync({ id, data });
  };

  const deleteRoom = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  return (
    <RoomContext.Provider
      value={{
        rooms,
        isLoading,
        error,
        roomData,
        isDataLoading,
        createRoom,
        updateRoom,
        deleteRoom,
        isSubmitting: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
        setError,
        refetchRooms
      }}
    >
      {children}
    </RoomContext.Provider>
  );
} 